import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator } from '@/components/ui/command';
import { Plus, CaretUpDown, Check } from '@phosphor-icons/react';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

export function CustomerCombo({ customers, value, onSelect, onAddNew, disabled, triggerRef }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Tracks which CommandItem is keyboard-highlighted. We seed it from the
  // current `value` so re-opening the combo lands the highlight on the
  // already-selected row instead of always snapping to the top.
  const [highlighted, setHighlighted] = useState(value || '');
  // Customer group lookup so each dropdown row can show the group name on
  // line 3. Loaded lazily the first time the popover opens.
  const [groupsById, setGroupsById] = useState({});
  const selected = customers.find(c => c.id === value);

  useEffect(() => {
    if (!open || Object.keys(groupsById).length > 0) return;
    api.get('/customer-groups').then(r => {
      const map = {};
      (r.data || []).forEach(g => { map[g.id] = g.name; });
      setGroupsById(map);
    }).catch(() => {});
  }, [open, groupsById]);

  // Keep `highlighted` in sync when the popover opens or `value` changes
  // externally — without this, picking a customer then re-opening would
  // still show the highlight on whatever row the cursor last visited.
  React.useEffect(() => {
    if (open) setHighlighted(value || '');
    if (!open) setSearch('');
  }, [open, value]);

  // Returns the list the dropdown is showing for the current search.
  // Used by the Enter-key handler so we can decide between (a) selecting
  // the one obvious match vs (b) triggering "Add new customer".
  const visibleMatches = () => {
    if (!search) return customers;
    const q = search.toLowerCase();
    const norm = q.replace(/[\s-+]/g, '');
    return customers.filter(c =>
      c.name.toLowerCase().includes(q) ||
      (c.phone || '').toLowerCase().includes(q) ||
      (c.phone || '').replace(/[\s-+]/g, '').includes(norm)
    );
  };

  // Intercept Enter on the search input so the keyboard flow mirrors the
  // mouse flow: when there's no match, we open the "Add New Customer"
  // dialog with the typed value pre-filled — digits land in Mobile,
  // anything else lands in Name (handled by CreateInvoicePage.onAddNew).
  const handleKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    const q = (search || '').trim();
    if (!q) return;
    const matches = visibleMatches();
    if (matches.length === 1) {
      // Single obvious match → pick it (avoids "Add new" friction).
      e.preventDefault();
      onSelect(matches[0].id);
      setOpen(false);
      setSearch('');
      return;
    }
    if (matches.length === 0 && onAddNew) {
      e.preventDefault();
      onAddNew(q);
      setOpen(false);
      setSearch('');
    }
  };

  return (
    <Popover open={open && !disabled} onOpenChange={(v) => !disabled && setOpen(v)}>
      <PopoverTrigger asChild>
        <Button ref={triggerRef} variant="outline" role="combobox" aria-expanded={open} disabled={disabled}
          className="w-full justify-between rounded-lg h-11 text-sm font-normal border-[#E4E4E7] hover:border-[#A1A1AA] hover:bg-white disabled:opacity-70 disabled:cursor-not-allowed"
          data-testid="customer-select"
        >
          <span className={selected ? 'text-[#0A0A0A]' : 'text-[#A1A1AA]'}>
            {selected ? `${selected.name} - ${selected.phone}` : 'Search customer name or mobile...'}
          </span>
          <CaretUpDown size={14} className="ml-2 shrink-0 text-[#A1A1AA]" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 rounded-lg" align="start">
        <Command shouldFilter={false} value={highlighted} onValueChange={setHighlighted}>
          <CommandInput placeholder="Type name or mobile..." value={search} onValueChange={setSearch} onKeyDown={handleKeyDown} data-testid="customer-select-input" />
          {/* Live duplicate hint: when the user is typing a phone-like query
              and it matches an EXACT phone of an existing customer, surface a
              prominent banner above the list so they don't accidentally create
              a duplicate. Click → select the existing customer. */}
          {(() => {
            const trimmed = (search || '').trim();
            if (!trimmed) return null;
            const isPhoneLike = /^[+]?\d[\d\s-]*$/.test(trimmed);
            if (!isPhoneLike) return null;
            const normalized = trimmed.replace(/[\s-+]/g, '');
            const hits = customers.filter(c => (c.phone || '').replace(/[\s-+]/g, '') === normalized);
            if (hits.length === 0) return null;
            return (
              <div className="border-b border-[#FDE68A] bg-[#FFFBEB] px-3 py-2" data-testid="dup-phone-banner">
                <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#92400E] mb-1">
                  {hits.length === 1 ? 'Mobile already saved' : `${hits.length} customers share this mobile`}
                </p>
                <div className="space-y-1">
                  {hits.slice(0, 3).map(h => (
                    <button
                      key={h.id}
                      type="button"
                      onClick={() => { onSelect(h.id); setOpen(false); setSearch(''); }}
                      className="w-full text-left text-xs text-[#92400E] hover:text-[#0A0A0A] hover:bg-white rounded px-2 py-1 flex items-center justify-between"
                      data-testid={`dup-phone-pick-${h.id}`}
                    >
                      <span><span className="font-semibold">{h.name}</span> — {h.phone}</span>
                      <span className="text-[10px] text-[#52525B]">Click to select →</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}
          <CommandList>
            <CommandEmpty className="py-3 text-center text-xs text-[#A1A1AA]">No customer found for &quot;{search}&quot;</CommandEmpty>
            <CommandGroup>
              {customers
                .filter(c => {
                  if (!search) return true;
                  const q = search.toLowerCase();
                  return c.name.toLowerCase().includes(q) || (c.phone || '').includes(q);
                })
                .map(c => {
                  // Per-customer secondary line layout (per user spec):
                  //   Line 1: name
                  //   Line 2: mobile  ·  address
                  //   Line 3: group name (if assigned)
                  const phoneAddrParts = [c.phone, c.address].filter(Boolean);
                  const groupName = groupsById[c.group_id || ''] || '';
                  return (
                <CommandItem key={c.id} value={c.id} onSelect={() => { onSelect(c.id); setOpen(false); setSearch(''); }}
                  className="flex items-center justify-between cursor-pointer">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-[#EFF6FF] flex items-center justify-center text-[10px] font-bold text-[#3B82F6]">{c.name.charAt(0)}</div>
                    <div>
                      <p className="text-sm font-medium leading-tight">{c.name}</p>
                      <p className="text-[10px] text-[#A1A1AA] truncate max-w-[260px] leading-tight">{phoneAddrParts.join(' · ')}</p>
                      {groupName && (
                        <p className="text-[10px] font-semibold text-[#3B82F6] truncate max-w-[260px] leading-tight uppercase tracking-wide">{groupName}</p>
                      )}
                    </div>
                  </div>
                  {c.id === value && <Check size={14} className="text-[#3B82F6]" />}
                </CommandItem>
                  );
                })}
            </CommandGroup>
            {onAddNew && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem onSelect={() => { onAddNew(search); setOpen(false); setSearch(''); }}
                    className="cursor-pointer text-[#3B82F6] font-medium">
                    <Plus size={14} className="mr-1.5" />
                    {search ? `Add "${search}" as new customer` : 'Add New Customer'}
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function ServiceCombo({ services, value, onSelect, onAddNew, testId, disabled, triggerRef }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Keyboard-highlighted row — seeded from `value` so re-opening a service
  // combo on a row that already has a service lands the highlight on that
  // service instead of jumping to the top of the list.
  const [highlighted, setHighlighted] = useState(value || '');
  const selected = services.find(s => s.id === value);

  React.useEffect(() => {
    if (open) setHighlighted(value || '');
    if (!open) setSearch('');
  }, [open, value]);

  return (
    <Popover open={open && !disabled} onOpenChange={(v) => !disabled && setOpen(v)}>
      <PopoverTrigger asChild>
        <Button ref={triggerRef} variant="outline" role="combobox" aria-expanded={open} disabled={disabled}
          className="w-full justify-between rounded-lg h-9 text-sm font-normal border-[#E4E4E7] hover:border-[#A1A1AA] hover:bg-white min-w-[200px] disabled:opacity-70 disabled:cursor-not-allowed"
          data-testid={testId}
        >
          <span className={`truncate ${selected ? 'text-[#0A0A0A]' : 'text-[#A1A1AA]'}`}>
            {selected ? (
              <>
                {selected.code && (
                  <span
                    className="inline-flex items-center justify-center mr-1.5 px-1 py-0.5 rounded text-[9px] font-bold font-mono bg-[#EFF6FF] text-[#1D4ED8] border border-[#BFDBFE] align-middle"
                    data-testid={`${testId}-code`}
                  >{selected.code}</span>
                )}
                {selected.name}
                {selected.govt_fee > 0 && (
                  <span className="ml-2 text-[10px] text-[#A1A1AA] font-mono align-middle">GF {fmt(selected.govt_fee)}</span>
                )}
              </>
            ) : 'Search service...'}
          </span>
          <CaretUpDown size={12} className="ml-1 shrink-0 text-[#A1A1AA]" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-0 rounded-lg" align="start">
        <Command shouldFilter={false} value={highlighted} onValueChange={setHighlighted}>
          <CommandInput placeholder="Type code or service name..." value={search} onValueChange={setSearch} data-testid={`${testId}-input`} />
          <CommandList>
            <CommandEmpty className="py-3 text-center text-xs text-[#A1A1AA]">No service found for &quot;{search}&quot;</CommandEmpty>
            <CommandGroup>
              {(() => {
                // Expand price-based services into one row per variant so the
                // operator can pick a variant directly from the search list.
                const expanded = [];
                services.forEach((s) => {
                  if (s.multi_price && s.tier_mode === 'price' && Array.isArray(s.price_tiers) && s.price_tiers.length) {
                    s.price_tiers.forEach((t) => expanded.push({
                      ...s,
                      _variant: { label: t.label, price: Number(t.price) || 0 },
                      _rowKey: `${s.id}__${t.label}`,
                    }));
                  } else {
                    expanded.push({ ...s, _rowKey: s.id });
                  }
                });
                return expanded
                  .filter((s) => {
                    if (!search) return true;
                    const q = search.toLowerCase().trim();
                    return s.name.toLowerCase().includes(q)
                      || (s.code || '').toLowerCase().includes(q)
                      || (s._variant?.label || '').toLowerCase().includes(q);
                  })
                  .map((s) => (
                <CommandItem key={s._rowKey} value={s._rowKey} onSelect={() => { onSelect(s); setOpen(false); setSearch(''); }}
                  className="flex items-center justify-between cursor-pointer">
                  <div className="flex-1 min-w-0 flex items-center gap-2">
                    {s.code ? (
                      <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded text-[10px] font-bold font-mono bg-[#EFF6FF] text-[#1D4ED8] border border-[#BFDBFE] shrink-0">{s.code}</span>
                    ) : (
                      <span className="inline-flex items-center justify-center w-6 shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">
                        {s.name}
                        {s._variant && (
                          <span className="ml-1.5 inline-flex items-center px-1.5 py-0 rounded text-[10px] font-semibold bg-[#F5F3FF] text-[#6D28D9] border border-[#DDD6FE] align-middle">
                            {s._variant.label}
                          </span>
                        )}
                        {s.multi_price && s.tier_mode !== 'price' && (
                          <span
                            className="ml-1.5 inline-flex items-center px-1 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-[#FFF7ED] text-[#C2410C] border border-[#FED7AA] align-middle"
                            title={`Volume-based · ${(s.price_tiers || []).length} tiers`}
                          >Tiered</span>
                        )}
                        <span className="ml-2 text-[10px] font-mono text-[#10B981] align-middle">
                          Svc {s._variant
                            ? fmt(s._variant.price)
                            : (s.multi_price && s.tier_mode === 'volume' && s.price_tiers?.length
                                ? `${fmt(Math.min(...s.price_tiers.map(t => Number(t.price) || 0)))}+`
                                : fmt(s.service_charge))}
                        </span>
                        {s.govt_fee > 0 && (
                          <span className="ml-1.5 text-[10px] font-mono text-[#A1A1AA] align-middle">· GF {fmt(s.govt_fee)}</span>
                        )}
                      </p>
                      {s.category && <p className="text-[10px] text-[#A1A1AA] truncate">{s.category}</p>}
                    </div>
                  </div>
                  {s.id === value && !s._variant && <Check size={14} className="text-[#3B82F6] ml-2 shrink-0" />}
                </CommandItem>
              ));
              })()}
            </CommandGroup>
            {onAddNew && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem onSelect={() => { onAddNew(search); setOpen(false); setSearch(''); }}
                    className="cursor-pointer text-[#3B82F6] font-medium">
                    <Plus size={14} className="mr-1.5" />
                    {search ? `Add "${search}" as new service` : 'Add New Service'}
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
